import React, { useState, useCallback } from 'react'
import {
    Box,
    CircularProgress,
    makeStyles,
    Tab,
    Tabs,
    Theme,
    Typography,
} from '@material-ui/core'
import { Company } from '../../../../models'
import { OrganizationSampleCOIs } from './OrganizationSampleCOIs'
import { OrganizationVendorCOIs } from './OrganizationVendorCOIs'
import { OrganizationCompliance } from './OrganizationCompliance'

type InsuranceTabType = 'sample' | 'vendor' | 'compliance'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
    },
    tab: {
        textTransform: 'none',
        minWidth: 120,
        fontWeight: 500,
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
    },
}))

interface OrganizationInsuranceProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
    propertyList: Company[]
}

// Loading fallback component
const TabLoading: React.FC = () => {
    const classes = useStyles()
    return (
        <Box className={classes.loadingContainer}>
            <CircularProgress size={32} />
        </Box>
    )
}

export const OrganizationInsurance: React.FC<OrganizationInsuranceProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
    propertyList,
}) => {
    const classes = useStyles()
    const [activeTab, setActiveTab] = useState<InsuranceTabType>('sample')

    const handleTabChange = useCallback(
        (_event: React.ChangeEvent<{}>, newValue: InsuranceTabType) => {
            setActiveTab(newValue)
        },
        [],
    )

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography
                    variant="h5"
                    component="h2"
                    className={classes.title}
                >
                    Insurance Manager
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Monitor vendor compliance and manage insurance requirements
                    across all properties.
                </Typography>
            </Box>

            <Box className={classes.tabsContainer}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab
                        label="Sample COIs"
                        value="sample"
                        className={classes.tab}
                    />
                    <Tab
                        label="Vendor COIs"
                        value="vendor"
                        className={classes.tab}
                    />
                    <Tab
                        label="Compliance"
                        value="compliance"
                        className={classes.tab}
                    />
                </Tabs>
            </Box>

            {activeTab === 'sample' && (
                <OrganizationSampleCOIs
                    organization={organization}
                    selectedTagId={selectedTagId}
                    onSelectedTagIdChange={onSelectedTagIdChange}
                    propertyList={propertyList}
                />
            )}
            {activeTab === 'vendor' && (
                <OrganizationVendorCOIs
                    organization={organization}
                    selectedTagId={selectedTagId}
                    onSelectedTagIdChange={onSelectedTagIdChange}
                />
            )}
            {activeTab === 'compliance' && (
                <OrganizationCompliance
                    organization={organization}
                    selectedTagId={selectedTagId}
                    onSelectedTagIdChange={onSelectedTagIdChange}
                />
            )}
        </Box>
    )
}
